import type { ReasonEvidence } from '@/entities/mid-term-prediction'
import type { ForecastPeriodModel, ReasonModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { pickEvidence } from '@/entities/mid-term-prediction'

import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'

import { patternLabel } from '../lib/labels'
import { formatPeriod } from '../lib/period'

const REASON_COPY: Record<
	ReasonModel['code'],
	{ key: string; defaultValue: string }
> = {
	intermittent: {
		key: 'midterm_forecast.reason.intermittent',
		defaultValue:
			'Demand is too intermittent — sales arrive in sparse, irregular spikes',
	},
	lumpy: {
		key: 'midterm_forecast.reason.lumpy',
		defaultValue:
			'Demand is too lumpy — both order timing and size vary strongly',
	},
	is_new: {
		key: 'midterm_forecast.reason.is_new',
		defaultValue:
			'The pricing line is too new — no established sales trajectory yet',
	},
	dormant: {
		key: 'midterm_forecast.reason.dormant',
		defaultValue: 'The pricing line has gone dormant — no recent sales',
	},
	not_enough_data: {
		key: 'midterm_forecast.reason.not_enough_data',
		defaultValue: 'Not enough sales history for a reliable forecast',
	},
	unknown: {
		key: 'midterm_forecast.reason.unknown',
		defaultValue: "The reason couldn't be determined",
	},
}

const evidenceText = (evidence: ReasonEvidence, t: TFunction): string => {
	switch (evidence.kind) {
		case 'age':
			return t('midterm_forecast.evidence_age', {
				defaultValue: '{daysLive} of the {minDays} days live it needs',
				defaultValue_one: '{daysLive} of the {minDays} day live it needs',
				count: evidence.minDays,
				daysLive: evidence.daysLive,
				minDays: evidence.minDays,
			})
		case 'dormancy':
			return t('midterm_forecast.evidence_dormancy', {
				defaultValue:
					'No sale for {daysSinceLastSale} days — the limit is {maxDays}',
				defaultValue_one:
					'No sale for {daysSinceLastSale} day — the limit is {maxDays}',
				count: evidence.daysSinceLastSale,
				daysSinceLastSale: evidence.daysSinceLastSale,
				maxDays: evidence.maxDays,
			})
		case 'pattern':
			return t('midterm_forecast.evidence_pattern', {
				defaultValue: 'Demand pattern {pattern}; forecastable: {allowed}',
				pattern: patternLabel(evidence.pattern, t),
				allowed: evidence.allowed.map((p) => patternLabel(p, t)).join(', '),
			})
	}
}

const ReasonEvidenceLine = ({ reason }: { reason: ReasonModel }) => {
	const { t } = useTranslation()
	const evidence = pickEvidence(reason)

	if (!evidence) return null

	return (
		<span className='text-muted-foreground'>{evidenceText(evidence, t)}</span>
	)
}

export const NotForecastableBanner = ({
	reasons,
	period,
}: {
	reasons: ReasonModel[]
	period?: ForecastPeriodModel | null
}) => {
	const { t } = useTranslation()

	return (
		<Alert variant='warning' data-testid='mtf-blocked-banner'>
			<AlertTriangleIcon className='size-4' />
			<AlertTitle>
				{t('midterm_forecast.blocked_title', {
					defaultValue: 'No mid-term forecast for this pricing line',
				})}
			</AlertTitle>
			<AlertDescription>
				{period && (
					<p className='text-xs' data-testid='mtf-blocked-period'>
						{t('midterm_forecast.blocked_period', {
							defaultValue: 'No forecast for {period}',
							period: formatPeriod(period.starts_on, period.ends_on),
						})}
					</p>
				)}
				<ul className='mt-1 flex flex-col gap-1'>
					{reasons.map((reason) => {
						const copy = REASON_COPY[reason.code] ?? REASON_COPY.unknown

						return (
							<li
								key={`${reason.factor}-${reason.code}`}
								data-testid={`mtf-reason-${reason.code}`}
								className='flex flex-col text-sm'
							>
								<span>{t(copy.key, { defaultValue: copy.defaultValue })}</span>
								<ReasonEvidenceLine reason={reason} />
							</li>
						)
					})}
				</ul>
			</AlertDescription>
		</Alert>
	)
}
